import * as Sentry from "@sentry/react";
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { verifyI18nParity } from './lib/i18n';

// Initialize Sentry only in production and only if VITE_SENTRY_DSN is configured.
// VITE_SENTRY_DSN must be set in Vercel environment variables (production environment).
if (import.meta.env.MODE === "production" && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_COMMIT_SHA as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Low sample rates — legal SaaS with strict data handling requirements.
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0.0,   // never sample normal sessions
    replaysOnErrorSampleRate: 1.0,   // always capture replays on error
    beforeSend(event) {
      // Strip any PII that leaked into exception values
      if (event.exception?.values) {
        event.exception.values.forEach((ex) => {
          if (ex.value) ex.value = ex.value.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "[REDACTED]");
        });
      }
      return event;
    },
  });

  // Wire up the logger's error forwarding hook (see app/src/lib/logger.ts)
  (window as any).__SENTRY_EARLY_CAPTURE__ = (err: Error, extra?: Record<string, unknown>) => {
    Sentry.captureException(err, { extra });
  };
}

// Dev-only i18n parity guard (L1.3). Build-time key parity is already enforced by
// `tsc --noEmit` via the `Record<TranslationKey, string>` typing; this surfaces
// empty/whitespace translations that the type system cannot catch.
if (import.meta.env.DEV) {
  const problems = verifyI18nParity();
  if (problems.length > 0) {
    console.error('[i18n] parity problems detected:', problems);
  }
}

if ('serviceWorker' in navigator) {
  const hasRecentRecovery = (key: string, windowMs: number) => {
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) return false;
    return Date.now() - timestamp < windowMs;
  };

  const markRecovery = (key: string) => {
    sessionStorage.setItem(key, String(Date.now()));
  };

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        const triggerUpdate = () => {
          if (registration.waiting) {
            registration.waiting.postMessage('SKIP_WAITING');
          }
        };

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed') {
              triggerUpdate();
            }
          });
        });

        // Catch fast deploys where a waiting worker already exists at startup.
        triggerUpdate();
      })
      .catch((error) => {
        console.error('Service worker registration failed', error);
      });
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const didReloadForSw = hasRecentRecovery('iamv:sw-controller-reload-at', 15000);
    if (!didReloadForSw) {
      markRecovery('iamv:sw-controller-reload-at');
      window.location.reload();
    }
  });

  const handleChunkRecovery = () => {
    const didRecover = hasRecentRecovery('iamv:chunk-recovery-at', 30000);
    if (!didRecover) {
      markRecovery('iamv:chunk-recovery-at');
      void navigator.serviceWorker.getRegistration().then((registration) => registration?.update());
      window.location.reload();
    }
  };

  window.addEventListener('error', (event) => {
    const target = event.target as HTMLScriptElement | null;
    if (target && target.tagName === 'SCRIPT' && target.src.includes('/assets/')) {
      handleChunkRecovery();
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason instanceof Error ? event.reason.message : String(event.reason || '');
    if (/ChunkLoadError|Failed to fetch dynamically imported module/i.test(message)) {
      handleChunkRecovery();
    }
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ThemeProvider>
  </StrictMode>,
);
